---
title: "Configuring automatic backups"
slug: "configuring-automatic-backups"
category: "admin"
description: "Point BookBurrow at OneDrive, SharePoint, a USB stick, or a network share and stop worrying about data loss."
tags: ["backup", "onedrive", "sharepoint", "usb", "network", "restore"]
order: 1
---

If you do one thing on your first day with BookBurrow, do this. Backups take two minutes to set up and mean that a dead hard drive, a stolen laptop, or an accidental deletion costs you — at most — 15 minutes of work instead of everything you've ever catalogued.

## How BookBurrow backs up

- **Every 15 minutes**, automatically, while the app is running.
- Writes a **full snapshot** of the library database (borrowers, books, loan history, settings) to a folder you choose.
- Keeps **30 days** of snapshots by default — oldest ones are pruned automatically so the folder doesn't grow forever.
- Each snapshot is a single file of a few megabytes, so it's small enough to live anywhere.

Backups are independent of the live database, so even if the main file somehow gets corrupted, yesterday's backup is still fine. Restoring from a backup is a three-click job — see [Restoring from a backup](/help/guides/restoring-from-a-backup).

## Choosing a good location

In order of "how sleep-soundly it lets you":

<Callout variant="info" title="Best options">
1. **OneDrive or SharePoint** (if your school uses Microsoft 365). Backups end up in the cloud automatically, survive a hardware failure, and can be accessed from any school computer.
2. **A network drive** maintained by your school IT. Same benefit as OneDrive, with the upside that IT probably backs *that* up too.
3. **A USB stick** left plugged into the library computer. Simple, works offline, but breaks if the stick is lost or the library computer walks off.
4. **A folder on the local hard drive**. Better than nothing (it protects against BookBurrow database corruption) but leaves you exposed if the hard drive dies.
</Callout>

## Setting up backups

<Steps>
  <Step title="Open Settings → Backups">
    You'll need the admin PIN.
  </Step>
  <Step title="Click Choose folder">
    A file picker opens. Navigate to the folder you want. For OneDrive, this is usually `C:\Users\[you]\OneDrive - [School Name]\BookBurrow Backups`.
  </Step>
  <Step title="Click Test backup">
    BookBurrow writes a small test file to the folder and reads it back. If the folder isn't writable (e.g. the USB stick fell out), you'll get a clear error now rather than at 3am next Tuesday.
  </Step>
  <Step title="Set the retention window">
    Default is 30 days. If your backup location has limited space (e.g. a small USB stick), you can reduce it. If you want longer history, you can go up to 365.
  </Step>
  <Step title="Click Save">
    Backups start immediately — you'll see a timestamp that updates every 15 minutes in the status bar.
  </Step>
</Steps>

## Making sure it's actually working

Come back in half an hour and check the backup folder. You should see two or three `.bbbackup` files named with timestamps. If you don't, something is wrong — the most common cause is that the folder location moved or got locked.

Every time BookBurrow starts, it also does a quick "can I still write to the backup folder?" check. If the check fails, you'll see a yellow warning banner at the top of the app. Click the banner for details.

## Additional ad-hoc backups

You can always run an extra backup at any point: **File → Backup now**. Good moments:

- Before running [year-end promotion](/help/guides/year-end-promotion).
- Before importing a large CSV.
- Before doing anything that makes you nervous.
- Before a BookBurrow version update (though updates don't touch the database — this is belt-and-braces).

The manual backup goes to the same folder as automatic backups.

## Restoring

If the worst happens, restoring is covered in [Restoring from a backup](/help/guides/restoring-from-a-backup). The short version: pick the most recent good backup, click Restore, confirm. The app restarts and you're back where you were.
